// Additional data — values in ZAR (Rand)

export const currentVsPotential = [
  { metric: "Equipment Uptime", current: "87.4%", potential: "94%+", impact: "+6.6 pts → ~R185m/yr revenue lift" },
  { metric: "Maintenance Approach", current: "Reactive", potential: "Predictive (IoT + ML)", impact: "−35% unplanned downtime · −18% maintenance cost (R75m/yr)" },
  { metric: "Production Output", current: "1.24M t/yr", potential: "1.45M t/yr", impact: "+17% throughput from crusher debottleneck (R407m revenue)" },
  { metric: "Data Usage", current: "Fragmented (5 systems)", potential: "Integrated (single pane)", impact: "Cycle time analytics → 8% productivity gain (R65m/yr)" },
  { metric: "Energy Mix", current: "100% Eskom grid", potential: "30% on-site solar PPA", impact: "−18% energy cost · ~R85m/yr" },
];

export const valueLevers = [
  {
    icon: "Activity",
    title: "Operational Efficiency",
    description: "Improve uptime, reduce idle equipment time, and optimise crew/asset scheduling across shifts.",
    benefit: "+6.6 pts uptime · 12% fewer idle hours",
    accent: "amber",
  },
  {
    icon: "Wrench",
    title: "Maintenance Optimisation",
    description: "Shift from reactive to predictive maintenance using vibration, oil and thermal sensor telemetry.",
    benefit: "−35% unplanned downtime · −R75m/yr cost",
    accent: "blue",
  },
  {
    icon: "TrendingUp",
    title: "Production Optimisation",
    description: "Better alignment of nameplate capacity to actual output by removing crusher and haul-cycle bottlenecks.",
    benefit: "+17% throughput · +R407m revenue/yr",
    accent: "green",
  },
  {
    icon: "Coins",
    title: "Cost Optimisation",
    description: "Identify inefficiencies in fuel, energy, contractor spend and consumables; reallocate to high-yield areas.",
    benefit: "−R148m/yr OpEx run-rate within 12 months",
    accent: "slate",
  },
  {
    icon: "Handshake",
    title: "Commercial Upside",
    description: "Diversify off-take, extend asset life through reserve upgrades, expand processing-as-a-service for nearby tenements.",
    benefit: "+4 yrs reserve life · base→upside R5.74bn → R7.12bn",
    accent: "amber",
  },
];

export const turnaroundRoadmap = [
  {
    phase: "Phase 1",
    duration: "0 – 90 Days",
    title: "Stabilise & See",
    items: [
      "Consolidate 5 source systems into a unified data lake",
      "Stand-up real-time operational dashboards (this platform)",
      "Identify quick wins via baseline diagnostics",
      "Lock in supplier rationalisation candidates",
    ],
    color: "#0F172A",
  },
  {
    phase: "Phase 2",
    duration: "3 – 6 Months",
    title: "Optimise & Align",
    items: [
      "Deploy predictive maintenance on top 12 critical assets",
      "Lift fleet uptime to 91%+",
      "Roll out shift-aligned planning workflows",
      "Begin solar PPA negotiations",
    ],
    color: "#B45309",
  },
  {
    phase: "Phase 3",
    duration: "6 – 12 Months",
    title: "Scale & Compound",
    items: [
      "Crusher debottlenecking → +17% throughput",
      "Reduce OpEx run-rate by R148m/yr",
      "Upgrade probable reserves via 2025 drilling campaign",
      "Pursue mid-tier export off-take to diversify clients",
    ],
    color: "#059669",
  },
];

export const techStack = [
  { layer: "Decision & Workflow", desc: "Cross-functional dashboards, alerting, IC-grade reporting", items: ["MAIP Dashboards", "Slack/Teams alerts", "Approval workflows"] },
  { layer: "Insight & AI", desc: "Predictive maintenance, AI risk insights, demand forecasting", items: ["Anomaly detection", "Claude Sonnet insights", "Cost forecasting"] },
  { layer: "Integration & Data", desc: "Single source of truth — operational, financial, geological", items: ["ERP · CMMS · LIMS", "IoT telemetry", "Geological models"] },
  { layer: "Field & Sensor", desc: "Telemetry from haul trucks, drills, crushers, conveyors", items: ["Vibration & thermal", "Fuel & idle sensors", "GPS / load weighing"] },
];

export const valueImpact = [
  { label: "Production uplift", value: "+17%", note: "1.24M → 1.45M tons/yr", accent: "green" },
  { label: "Downtime reduction", value: "−35%", note: "Unplanned events", accent: "amber" },
  { label: "OpEx reduction", value: "−R148m", note: "Annual run-rate", accent: "slate" },
  { label: "Profitability lift", value: "+R555m", note: "EBITDA, 24-mo run-rate", accent: "green" },
];

// WORKFORCE
export const workforce = {
  totalEmployees: 1240,
  contractors: 380,
  consultants: 45,
  unionMembership: "62%",
  attritionAnnual: "4.8%",
  averageTenure: "7.2 yrs",
};

export const skillDistribution = [
  { name: "Skilled (Engineers, Technicians, Geologists)", value: 28, color: "#0F172A" },
  { name: "Semi-skilled (Operators, Artisans)", value: 47, color: "#B45309" },
  { name: "Unskilled (General workers)", value: 25, color: "#94A3B8" },
];

export const departments = [
  { name: "Mining Operations", head: "GM Operations", headcount: 540, key: "Pit dispatch, haulage, drill & blast" },
  { name: "Maintenance", head: "Maintenance Manager", headcount: 220, key: "Mobile fleet, plant, conveyors" },
  { name: "Processing & Plant", head: "Plant Manager", headcount: 180, key: "Crushing, screening, beneficiation" },
  { name: "Geology & Survey", head: "Chief Geologist", headcount: 38, key: "Resource modelling, grade control" },
  { name: "SHEQ", head: "SHEQ Manager", headcount: 42, key: "Safety, health, environmental, quality" },
  { name: "Finance & Commercial", head: "CFO", headcount: 65, key: "Reporting, off-take, procurement" },
  { name: "Human Resources", head: "HR Manager", headcount: 48, key: "Industrial relations, training" },
  { name: "Engineering & Projects", head: "Chief Engineer", headcount: 107, key: "Capex, design, expansion projects" },
];

export const keyRoles = [
  { role: "General Manager", count: 1, status: "Permanent · 8 yrs tenure" },
  { role: "Mine Captains", count: 6, status: "All filled" },
  { role: "Mining Engineers", count: 14, status: "All filled · 1 in succession pipeline" },
  { role: "Mechanical Engineers", count: 9, status: "All filled" },
  { role: "Section Geologists", count: 5, status: "4 filled · 1 vacant" },
  { role: "Senior Operators (Shift B)", count: 24, status: "19 filled · 5 vacant" },
];

// Workforce opportunities — savings in ZAR
export const workforceOpportunities = [
  { title: "Predictive maintenance upskilling", impact: "Train 30 artisans on telemetry-based diagnostics → reduce reliance on OEM specialists.", saving: "R22m/yr", phase: 2 },
  { title: "Shift B operator gap closure", impact: "Targeted recruitment + 3-month induction can lift Shift B uptime by ~4 pts.", saving: "R52m/yr", phase: 1 },
  { title: "Cross-skilled artisans", impact: "Multi-skilled mechanical/electrical artisans reduce job-card hand-off delays.", saving: "R17m/yr", phase: 2 },
  { title: "Apprenticeship pipeline", impact: "BBBEE-aligned apprenticeship with local TVET college builds long-term skills supply.", saving: "Long-term · CSI credit", phase: 3 },
];

// REGIONAL
export const regionalLocation = {
  region: "Limpopo Province · Mopani District Municipality",
  nearestTowns: [
    { name: "Tzaneen", distance: "8 km", role: "Service hub · banks, retail" },
    { name: "Polokwane", distance: "120 km", role: "Provincial capital · airport, hospitals" },
    { name: "Phalaborwa", distance: "95 km", role: "Established mining town · skilled labour pool" },
    { name: "Nelspruit", distance: "210 km", role: "Mpumalanga provincial centre" },
  ],
};

export const labourPool = {
  districtPopulation: "≈ 1.16 million",
  workingAge: "≈ 720,000 (15-64 yrs)",
  miningEmploymentLocal: "≈ 24,000 currently in mining sector",
  literacyRate: "82%",
  technicalCollegesNearby: 3,
  notes: "Mopani District has a young workforce with strong mining-adjacent skills (Phalaborwa Mining Co., Foskor, Anglo Mogalakwena nearby).",
};

export const infrastructure = [
  { icon: "Route", label: "Road Access", detail: "R71 + R529 connectivity · all-weather access for haulage", quality: "good" },
  { icon: "Train", label: "Rail", detail: "Transnet Tzaneen branch · 14 km to siding, links to N4/Maputo corridor", quality: "good" },
  { icon: "Zap", label: "Power", detail: "Eskom 132 kV grid connection · 35 MW peak demand · solar PPA potential", quality: "average" },
  { icon: "Droplets", label: "Water", detail: "Letaba River + 2 boreholes · WUL valid until 2031 · 4.2 Mℓ/day usage", quality: "good" },
  { icon: "Plane", label: "Air", detail: "Polokwane International (PTG) · 120 km · scheduled domestic flights", quality: "good" },
  { icon: "Wifi", label: "Comms", detail: "Fibre + LTE coverage on site · redundant satellite uplink", quality: "good" },
];

export const valueChain = [
  { tier: "Upstream / Suppliers", items: ["Local explosives (BME Pty)", "Diesel (Engen Phalaborwa)", "Steel & spares (Limpopo Industrial)", "Tyres (Bridgestone Polokwane)"] },
  { tier: "On-site Services", items: ["Catering (locally owned)", "Security (Tactical Security ZA)", "Logistics (Unitrans · Reinhardt)", "Lab assay (SGS Tzaneen)"] },
  { tier: "Downstream / Off-take", items: ["Eskom Power Stations", "ArcelorMittal Vanderbijlpark", "Glencore Trading hub", "Anglo American Refining"] },
  { tier: "Future Opportunities", items: ["Beneficiation toll-treating for nearby tenements", "Solar farm partnership", "Skills exchange with TVET colleges", "ESG-linked finance products"] },
];

export const strategicPositioning = {
  title: "Why this location supports long-term value",
  pillars: [
    {
      title: "Operational sustainability",
      body: "Stable climate (subtropical highveld), reliable water rights, mature road and rail logistics — all reduce the operational risk premium that often plagues remote African mining assets.",
    },
    {
      title: "Expansion potential",
      body: "Two adjacent farm portions are already under Section 11 transfer approval. A nearby chrome tenement is open for joint venture or toll-treating, leveraging spare capacity at the Tzaneen processing plant.",
    },
    {
      title: "Cost advantages",
      body: "Local skilled-labour catchment (24,000+ mining workers in Mopani), lower wage structure than Witwatersrand, and proximity to Maputo port give Tzaneen a R110–150/ton landed-cost advantage over inland competitors.",
    },
  ],
};
